import { Directive, Input, inject, OnDestroy, OnInit, TemplateRef, ViewContainerRef } from '@angular/core';
import { CurrentUserService } from '@services/auth/current-user.service';
import { Subscription } from 'rxjs';

/**
 * Structural directive that conditionally renders elements based on user permissions.
 *
 * @example Show element if user has permission
 * <button *appHasPermission="'products:delete'">Delete</button>
 *
 * @example Show element if user has ANY of multiple permissions
 * <button *appHasPermission="['products:update', 'products:delete']">Edit/Delete</button>
 *
 * @example With else template
 * <button *appHasPermission="'products:delete'; else noPermission">Delete</button>
 * <ng-template #noPermission>
 *   <span>No permission</span>
 * </ng-template>
 */
@Directive({
	selector: '[appHasPermission]',
	standalone: true,
})
export class HasPermissionDirective implements OnInit, OnDestroy {
	private currentUserService = inject(CurrentUserService);
	private templateRef = inject(TemplateRef<unknown>);
	private viewContainer = inject(ViewContainerRef);

	private permissions: string | string[] = [];
	private elseTemplateRef: TemplateRef<unknown> | null = null;
	private hasView = false;
	private subscription: Subscription | null = null;

	@Input()
	set appHasPermission(value: string | string[]) {
		this.permissions = value;
		this.updateView();
	}

	@Input()
	set appHasPermissionElse(templateRef: TemplateRef<unknown> | null) {
		this.elseTemplateRef = templateRef;
		this.updateView();
	}

	ngOnInit(): void {
		// Subscribe to user changes to update view when permissions change
		this.subscription = this.currentUserService.currentUser$.subscribe(() => {
			this.updateView();
		});
	}

	ngOnDestroy(): void {
		this.subscription?.unsubscribe();
	}

	private updateView(): void {
		const hasPermission = this.checkPermission();

		if (hasPermission && !this.hasView) {
			this.viewContainer.clear();
			this.viewContainer.createEmbeddedView(this.templateRef);
			this.hasView = true;
		} else if (!hasPermission && this.hasView) {
			this.viewContainer.clear();
			if (this.elseTemplateRef) {
				this.viewContainer.createEmbeddedView(this.elseTemplateRef);
			}
			this.hasView = false;
		} else if (!hasPermission && !this.hasView && this.elseTemplateRef) {
			this.viewContainer.clear();
			this.viewContainer.createEmbeddedView(this.elseTemplateRef);
		}
	}

	private checkPermission(): boolean {
		if (!this.permissions || (Array.isArray(this.permissions) && this.permissions.length === 0)) {
			return true; // No permission required
		}

		if (Array.isArray(this.permissions)) {
			return this.currentUserService.hasAnyPermission(this.permissions);
		}

		return this.currentUserService.hasPermission(this.permissions);
	}
}
